import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  Tooltip,
  Alert,
  Grid,
  Paper,
  Divider,
  Chip,
  Stack,
  useTheme,
  keyframes,
  styled
} from '@mui/material';
import { Download, Description, Speed, Engineering, DirectionsCar, SmartToy } from '@mui/icons-material';
import { UPLOAD_CONFIG } from '../config/environment';

// Keyframe animations (matching HomePage)
const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 4s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
}));

const TemplatePage: React.FC = () => {
  const theme = useTheme();
  // Template download function
  const handleDownloadTemplate = (
    templateType: 'empty' | 'example' | 'planning' |
                  'battery_robotics' | 'battery_evehicle' | 'battery_industrial'
  ) => {
    const apiUrl = UPLOAD_CONFIG.apiUrl || 'http://localhost:8000';
    let url = '';

    // Standard templates (timestamp format)
    if (templateType === 'empty') {
      url = `${apiUrl}/api/template/csv?type=empty`;
    } else if (templateType === 'example') {
      url = `${apiUrl}/api/template/csv?type=example&duration_hours=24&interval_minutes=15`;
    } else if (templateType === 'planning') {
      url = `${apiUrl}/api/template/csv?type=planning&duration_hours=168&interval_minutes=60`;
    }
    // Battery storage templates (zeit/leistung format)
    else if (templateType === 'battery_robotics') {
      url = `${apiUrl}/api/template/csv?type=battery_robotics&duration_seconds=150&interval_seconds=0.001`;
    } else if (templateType === 'battery_evehicle') {
      url = `${apiUrl}/api/template/csv?type=battery_evehicle&duration_seconds=300&interval_seconds=0.1`;
    } else if (templateType === 'battery_industrial') {
      url = `${apiUrl}/api/template/csv?type=battery_industrial&duration_seconds=200&interval_seconds=0.01`;
    }

    // Trigger download
    window.open(url, '_blank');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: 8
      }}
    >
      {/* Animated Background Layers (matching HomePage) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}12 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}10 0%, transparent 50%),
            radial-gradient(circle at 60% 40%, ${theme.palette.info.main}08 0%, transparent 50%)
          `,
          animation: `${pulse} 8s ease-in-out infinite`,
          zIndex: 0,
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="🔧 CSV-Vorlagen"
            color="primary"
            variant="outlined"
            sx={{
              mb: 3,
              fontSize: '1rem',
              py: 1,
              px: 3,
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
              background: `${theme.palette.primary.main}15`,
              border: `2px solid ${theme.palette.primary.main}50`,
            }}
          />
          <AnimatedTitle variant="h2">
            CSV-Vorlagen Download
          </AnimatedTitle>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              fontWeight: 300,
              opacity: 0.9
            }}
          >
            Keine eigenen Messdaten? Kein Problem! Laden Sie eine passende Vorlage herunter
            und füllen Sie diese mit Ihren Werten.
          </Typography>
        </Box>

        {/* Standard Templates Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 6,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.primary.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.primary.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}20`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 30% 40%, ${theme.palette.primary.main}10 0%, transparent 50%)`,
              borderRadius: 'inherit',
              zIndex: -1,
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Description sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight={700} color="primary">
                Standard-Lastprofile
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Allgemeine Lastprofile mit Zeitstempel-Format
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  background: `${theme.palette.primary.main}10`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.primary.main}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${theme.palette.primary.main}30`,
                    background: `${theme.palette.primary.main}15`,
                  },
                }}
              >
                <CardContent>
                  <Speed sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Leere Vorlage
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Nur Spaltenüberschriften mit Anleitung. Ideal für manuelle Dateneingabe von Grund auf.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => handleDownloadTemplate('empty')}
                    fullWidth
                  >
                    Herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  background: `${theme.palette.primary.main}10`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.primary.main}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${theme.palette.primary.main}30`,
                    background: `${theme.palette.primary.main}15`,
                  },
                }}
              >
                <CardContent>
                  <Speed sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Beispiel (24h)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    24h Beispieldaten mit realistischem Lastprofil. Perfekt zum Testen des Systems.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => handleDownloadTemplate('example')}
                    fullWidth
                  >
                    Herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  background: `${theme.palette.primary.main}10`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.primary.main}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${theme.palette.primary.main}30`,
                    background: `${theme.palette.primary.main}15`,
                  },
                }}
              >
                <CardContent>
                  <Speed sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Planungs-Vorlage (7 Tage)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    1 Woche zukünftige Zeitstempel für Lastprognosen und Planungen.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => handleDownloadTemplate('planning')}
                    fullWidth
                  >
                    Herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Format:</strong> <code>timestamp, current_A, voltage_V, power_W</code>
              <br />
              <strong>Empfehlung:</strong> Mindestens 24 Stunden Daten, Intervall 15 Minuten
            </Typography>
          </Alert>
        </Paper>

        {/* Battery Storage Templates Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 6,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.success.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.success.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.success.main}20`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 30% 40%, ${theme.palette.success.main}10 0%, transparent 50%)`,
              borderRadius: 'inherit',
              zIndex: -1,
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Engineering sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
            <Box>
              <Typography variant="h4" fontWeight={700} color="success.main">
                Batteriespeicher-Profile
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Spezialisierte Profile mit Energierückgewinnung (Rekuperation)
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  background: `${theme.palette.success.main}10`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.success.main}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${theme.palette.success.main}30`,
                    background: `${theme.palette.success.main}15`,
                  },
                }}
              >
                <CardContent>
                  <SmartToy sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    🤖 Robotik
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip label="1ms" size="small" color="success" />
                    <Chip label="150s" size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Arbeitszyklen mit Beschleunigung, Arbeit, Bremsen und Rekuperation.
                    Für Industrieroboter und Automatisierung.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={() => handleDownloadTemplate('battery_robotics')}
                    fullWidth
                  >
                    Herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  background: `${theme.palette.success.main}10`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.success.main}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${theme.palette.success.main}30`,
                    background: `${theme.palette.success.main}15`,
                  },
                }}
              >
                <CardContent>
                  <DirectionsCar sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    🚗 E-Fahrzeug
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip label="100ms" size="small" color="success" />
                    <Chip label="300s" size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Fahrzyklus mit Beschleunigung, Fahrt und regenerativem Bremsen.
                    Für E-Autos, E-Bikes, E-Scooter.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={() => handleDownloadTemplate('battery_evehicle')}
                    fullWidth
                  >
                    Herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  background: `${theme.palette.success.main}10`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.success.main}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${theme.palette.success.main}30`,
                    background: `${theme.palette.success.main}15`,
                  },
                }}
              >
                <CardContent>
                  <Engineering sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    🏭 Industrieanlage
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip label="10ms" size="small" color="success" />
                    <Chip label="200s" size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Produktionszyklen mit Lastspitzen und optionaler Rekuperation.
                    Für CNC-Maschinen, Pressen.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Download />}
                    onClick={() => handleDownloadTemplate('battery_industrial')}
                    fullWidth
                  >
                    Herunterladen
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Format:</strong> <code>zeit (Sekunden), leistung (Watt)</code>
              <br />
              <strong>Besonderheit:</strong> Negative Werte = Energierückgewinnung (Rekuperation)
            </Typography>
          </Alert>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Anwendungsfälle:</strong>
              <br />
              • 🤖 Robotik: Industrieroboter, Automatisierung, Pick-and-Place
              <br />
              • 🚗 E-Fahrzeug: E-Autos, E-Bikes, E-Scooter mit Bremsenergie
              <br />
              • 🏭 Industrie: Fertigungsmaschinen, CNC, Pressen mit Lastspitzen
            </Typography>
          </Alert>
        </Paper>

        {/* Usage Instructions */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}60 0%, ${theme.palette.warning.main}08 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.warning.main}30`,
            boxShadow: `0 8px 32px ${theme.palette.warning.main}20`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 30% 40%, ${theme.palette.warning.main}10 0%, transparent 50%)`,
              borderRadius: 'inherit',
              zIndex: -1,
            }
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            📝 Verwendung der Vorlagen
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                <strong>1.</strong> Passende Vorlage herunterladen
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>2.</strong> In Excel oder Text-Editor öffnen
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>3.</strong> Eigene Messdaten eintragen
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                <strong>4.</strong> Als CSV (UTF-8) speichern
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>5.</strong> Im Upload-Bereich hochladen
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>6.</strong> Analyse-Ergebnisse erhalten
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default TemplatePage;
